import React, { useState, useEffect, useRef } from 'react';

import useInterval from '../../../customHooks/useInterval';
import Character from './Character';
import Popup from '../../partials/OLIPopup';
import AudioControls from '../../UI/AudioPlayer';

const WalkingSim = props => {
    const CHARACTER_SPEED = 3;
   
    const FRAME_RATE = 60;
    const INTERVAL = 1000 / FRAME_RATE;

    const [characterPos, setCharacterPos] = useState({x: null, y: null});
    const [mousePos, setMousePos] = useState({ x: null, y: null });
    const [mouseDown, setMouseDown] = useState(false);
    const [characterAngle, setCharacterAngle] = useState(90);
    const [footprints, setFootprints] = useState([]);
    const [showIntro, setShowIntro] = useState(true);
    const [destinationReached, setDestinationReached] = useState(false);
    const [failure, setFailure] = useState(false);

    useEffect(() => {
        setCharacterPos(props.data.STARTING_COORDINATES);
    }, [props.data.STARTING_COORDINATES])

    useEffect(() => {
        if (failure) {
            // only executes during second walking sim
            props.audioController.removeAudio('bite_to_eat');
            props.audioController.playAudioByName('helen_retry');
        }
    }, [failure])

    useEffect(() => {
        props.audioController.playAudioByName(props.data.introAudio)
        return () => {
            props.audioController.removeAllAudio();
        }
    }, [])

    // used in place of setInterval, custom hook written by facebook guy
    useInterval(() => {
        if (mouseDown) moveCharacter();
    }, INTERVAL);
    

    function moveCharacter() {
        if (mousePos.x === null || mousePos.y === null || characterPos.x === null || characterPos.y === null) {
            return
        }

        const deltaX = mousePos.x - characterPos.x;
        const deltaY = mousePos.y - characterPos.y;

        if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) {
            // reduces glitchy behavior when character gets close to mouse
            return
        }

        const thetaRadians = Math.atan2(deltaY, deltaX);
        const thetaDegrees = radiansToDegrees(thetaRadians);

        const newPosition = {
            x: CHARACTER_SPEED * Math.cos(thetaRadians) + characterPos.x, 
            y: CHARACTER_SPEED * Math.sin(thetaRadians) + characterPos.y
        }

        setCharacterAngle(thetaDegrees); // convert to degrees
        if (!checkAllCollisions(newPosition)) {
            setCharacterPos({...newPosition});
        }
    }

    function radiansToDegrees(radians) {
        return radians * 180 / Math.PI
    }

    function  degreesToRadians(degrees) {
        return degrees / 180 * Math.PI
    }

    function handleMouseDown() {
        setMouseDown(true);
    }

    function handleMouseUp() {
        setMouseDown(false);
    }

    function handleMouseMove(e) {
        const mouseX = e.nativeEvent.offsetX;
        const mouseY = e.nativeEvent.offsetY;
         // ensure bounds are not outside of environment DIV, otherwise they won't register in mouseMove.
         // in other words, make the bounds 1 pixel smaller than environment element along x and y axes
        if (mouseX <= props.data.ENVIRONMENT_BOUNDS[0]  
            || mouseX >= props.data.ENVIRONMENT_BOUNDS[2] 
            || mouseY <= props.data.ENVIRONMENT_BOUNDS[1] 
            || mouseY >= props.data.ENVIRONMENT_BOUNDS[3]) {
            handleMouseUp();
        } else {
            if (mouseDown) {
                const newMousePos = {x: mouseX, y: mouseY};
                setMousePos({...newMousePos});
            }
        }
    }

    function checkAllCollisions(newPosition) {
        // simple boundary-overlap collision checking
        const characterBounds = {
            top: newPosition.y,
            right: newPosition.x + props.data.CHARACTER_BOUNDS[0],
            bottom: newPosition.y + props.data.CHARACTER_BOUNDS[1],
            left: newPosition.x
        }

        return props.data.collisionBoxes.some(e => checkCollision(e, characterBounds))
    }

    function checkCollision(box, characterBounds) {
        const boxBounds = {
            top: box.position[1],
            right: box.position[0] + box.dimensions[0],
            bottom: box.position[1] + box.dimensions[1],
            left: box.position[0]
        }

        if (characterBounds.top < boxBounds.bottom 
            && characterBounds.bottom > boxBounds.top
            && characterBounds.right > boxBounds.left
            && characterBounds.left < boxBounds.right) {
            if (box.isDestination && !destinationReached) {
                // First clean the previous audio
                props.audioController.removeAllAudio();
                // Then play the new audio
                props.audioController.playAudioByName(props.data.completionAudio)
                setDestinationReached(true);
            }
            if (box.isFailureTrigger) {
                setFailure(true);
                props.setSharedData({
                    ...props.sharedData,
                    visited: true,
                })
            }

            return true
        }
    }
    

    function leaveFootprint() {
        const footprint = {
            angle: characterAngle,
            x: characterPos.x-20,
            y: characterPos.y-10
        }
        const newFootprints = [...footprints, footprint];
        setFootprints(newFootprints);
    }

    return (
        <div className={`WalkingSim ${props.data.class || ''}`}>
            <AudioControls audioCollection={props.audioCollection}/>
            {showIntro && (props.sharedData.visited === undefined || props.sharedData.visited === false) && props.data.introPopup && 
                <Popup data={props.data.introPopup} onComplete={() => setShowIntro(false) } />

            }
            {destinationReached &&
                <Popup data={props.data.outroPopup} onComplete={() => {

                    props.onComplete()
                    props.setSharedData({
                        ...props.sharedData,
                        visited: false,
                    })
                }
            } />
            }
            {failure &&
                <Popup data={props.data.failurePopup} onComplete={props.reset} />
            }
            <div className={`environment`}>
                <div className={`instructions`} dangerouslySetInnerHTML={{__html: props.data.instructions}} />
                <div className={`interactive-layer`} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp} onMouseMove={handleMouseMove} />
                <Character 
                    onWalkInterval={leaveFootprint} 
                    isMoving={mouseDown} 
                    angle={characterAngle} 
                    position={characterPos} 
                    dimensions={props.data.CHARACTER_BOUNDS}
                />
                <div className={`assets`}>
                    {
                        props.data.assets.map((e,i) => {
                            return <div className={`asset asset-${i} ${e.class}`} />
                        })

                    }
                </div>
                <div className={`collision-boxes`}>
                    {
                        props.data.collisionBoxes.map((e,i) => {
                            return <div 
                                    className={`collision-box collision-box-${i} ${e.class ? e.class : ''}`} 
                                    key={`collision-box-${i}`} 
                                    style={{
                                        position: 'absolute',
                                        top: e.position[1],
                                        left: e.position[0],
                                        height: e.dimensions[1],
                                        width: e.dimensions[0],

                                    }}
                                    />
                        })
                    }
                </div>
                <div className={`footprints`}>
                    {
                        footprints.map((e,i) => {
                            return <div 
                                className={`footprint footprint-${i}`} 
                                style={{
                                    top: `${e.y}px`,
                                    left: `${e.x}px`,
                                    transform: `rotate(${e.angle}deg)`
                                }}
                            />
                        })
                    }
                </div>
                <div className={`background`} />
            </div>
        </div>
    )
}

export default WalkingSim